import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, ArrowLeft, ArrowRight, Calendar, Edit3, BarChart3, Target } from 'lucide-react';
import styles from './HabitDetailsTour.module.css';

interface HabitDetailsTourProps {
  isActive: boolean;
  onComplete: () => void;
}

interface TourStep {
  id: string;
  title: string;
  description: string;
  tip?: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  icon: React.ReactNode;
  highlight?: string;
}

const tourSteps: TourStep[] = [
  {
    id: 'habit-details-overview',
    title: 'Detalhes do Hábito',
    description: 'Aqui você pode ver todas as informações do seu hábito, editar configurações e acompanhar seu progresso detalhado.',
    tip: 'Clique em qualquer hábito na sua agenda para abrir esta tela.',
    position: 'bottom',
    icon: <Target className="w-5 h-5" />,
    highlight: 'modal-header'
  },  {
    id: 'edit-button',
    title: 'Editar Hábito',
    description: 'Use este botão para modificar o nome, descrição, duração e configurações do seu hábito.',
    tip: 'Você pode ativar valores personalizados para acompanhar métricas específicas como peso, repetições, etc.',
    position: 'bottom',
    icon: <Edit3 className="w-5 h-5" />,
    highlight: 'edit-button'
  },
  {
    id: 'weekly-chart',
    title: 'Seção dos Últimos 7 Dias - CLICÁVEL!',
    description: 'Esta seção mostra seu progresso na última semana. CLIQUE em qualquer lugar desta seção para abrir o calendário completo!',
    tip: 'Ao clicar aqui, você pode acessar o calendário onde pode marcar dias retroativamente.',
    position: 'top',
    icon: <Calendar className="w-5 h-5" />,
    highlight: 'weekly-chart'
  },
  {
    id: 'stats-section',
    title: 'Estatísticas Rápidas',
    description: 'Acompanhe sua sequência atual e taxa de sucesso. Use o botão de atualizar se precisar recalcular.',
    tip: 'A sequência só conta quando você mantém o hábito por dias consecutivos.',
    position: 'top',
    icon: <BarChart3 className="w-5 h-5" />,
    highlight: 'stats-section'
  },  {
    id: 'weekly-chart',
    title: 'Seção dos Últimos 7 Dias - CLICÁVEL!',
    description: 'Esta seção mostra seu progresso na última semana. CLIQUE em qualquer lugar desta seção para abrir o calendário completo!',
    tip: 'Ao clicar aqui, você pode acessar o calendário onde pode marcar dias retroativamente.',
    position: 'top',
    icon: <Calendar className="w-5 h-5" />,
    highlight: 'weekly-chart'
  },  {
    id: 'custom-values',
    title: 'Valores Personalizados',
    description: 'Se você ativou valores personalizados, aqui aparecerá o histórico dos seus registros (peso, repetições, etc.).',
    tip: 'Perfeito para hábitos quantificáveis como "beber X litros de água" ou "fazer X flexões".',
    position: 'top',
    icon: <Target className="w-5 h-5" />,
    highlight: 'custom-values'
  },
  {
    id: 'help-button',
    title: 'Botão de Ajuda',
    description: 'Este novo botão de ajuda (?) abre este guia sempre que você precisar relembrar como usar os detalhes do hábito.',
    tip: 'Substituímos o antigo botão de calendário por este botão de ajuda. Agora o calendário abre clicando na seção dos últimos 7 dias!',
    position: 'bottom',
    icon: <Target className="w-5 h-5" />,
    highlight: 'help-button'
  },
];

const HabitDetailsTour: React.FC<HabitDetailsTourProps> = ({ isActive, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isActive) return;

    const updateTooltipPosition = () => {
      const step = tourSteps[currentStep];
      if (!step.highlight) return;

      const element = document.querySelector(`[data-tour="${step.highlight}"]`) as HTMLElement;
      
      if (element && tooltipRef.current) {
        const rect = element.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        
        let top = rect.top;
        let left = rect.left + rect.width / 2 - tooltipRect.width / 2;

        // Ajustar posição baseada na propriedade position do step
        switch (step.position) {
          case 'top':
            top = rect.top - tooltipRect.height - 10;
            break;
          case 'bottom':
            top = rect.bottom + 10;
            break;
          case 'left':
            top = rect.top + rect.height / 2 - tooltipRect.height / 2;
            left = rect.left - tooltipRect.width - 10;
            break;
          case 'right':
            top = rect.top + rect.height / 2 - tooltipRect.height / 2;
            left = rect.right + 10;
            break;
        }

        // Ajustes para mobile - sempre centralizar horizontalmente
        if (isMobile) {
          left = window.innerWidth / 2 - tooltipRect.width / 2;
          
          // Se o tooltip ficaria muito em cima ou embaixo, ajustar
          if (top < 100) {
            top = 100;
          } else if (top + tooltipRect.height > window.innerHeight - 100) {
            top = window.innerHeight - tooltipRect.height - 100;
          }
        } else {
          // Ajustes para desktop para não sair da tela
          if (left < 10) left = 10;
          if (left + tooltipRect.width > window.innerWidth - 10) {
            left = window.innerWidth - tooltipRect.width - 10;
          }
          if (top < 10) top = 10;
          if (top + tooltipRect.height > window.innerHeight - 10) {
            top = window.innerHeight - tooltipRect.height - 10;
          }
        }

        setTooltipPosition({ top, left });
      }
    };

    // Destacar elemento atual
    const step = tourSteps[currentStep];
    if (step.highlight) {
      const element = document.querySelector(`[data-tour="${step.highlight}"]`) as HTMLElement;
      if (element) {
        element.classList.add(styles.highlighted);
        updateTooltipPosition();
      }
    }

    // Cleanup ao mudar de step
    return () => {
      const allHighlighted = document.querySelectorAll(`.${styles.highlighted}`);
      allHighlighted.forEach(el => el.classList.remove(styles.highlighted));
    };
  }, [currentStep, isActive, isMobile]);

  const nextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTour = () => {
    // Remover highlight de todos os elementos
    const allHighlighted = document.querySelectorAll(`.${styles.highlighted}`);
    allHighlighted.forEach(el => el.classList.remove(styles.highlighted));
    
    onComplete();
  };

  const skipTour = () => {
    completeTour();
  };

  if (!isActive) return null;

  const currentStepData = tourSteps[currentStep];

  return (
    <>
      {/* Overlay escuro */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={styles.overlay}
      />

      {/* Tooltip */}
      <motion.div
        ref={tooltipRef}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`${styles.tooltip} ${isMobile ? styles.tooltipMobile : ''}`}
        style={{
          position: 'fixed',
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          zIndex: 10001
        }}
      >
        {/* Header do tooltip */}
        <div className={styles.tooltipHeader}>
          <div className={styles.tooltipTitleRow}>
            <div className={styles.tooltipIcon}>
              {currentStepData.icon}
            </div>
            <h3 className={styles.tooltipTitle}>{currentStepData.title}</h3>
          </div>
          <button onClick={skipTour} className={styles.closeButton}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Conteúdo do tooltip */}
        <div className={styles.tooltipContent}>
          <p className={styles.tooltipDescription}>
            {currentStepData.description}
          </p>
          
          {currentStepData.tip && (
            <div className={styles.tooltipTip}>
              <span className={styles.tipIcon}>💡</span>
              <span className={styles.tipText}>{currentStepData.tip}</span>
            </div>
          )}
        </div>

        {/* Footer com navegação */}
        <div className={styles.tooltipFooter}>
          <div className={styles.stepIndicator}>
            {currentStep + 1} de {tourSteps.length}
          </div>
          
          <div className={styles.navigationButtons}>
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`${styles.navButton} ${styles.prevButton}`}
            >
              <ArrowLeft className="w-4 h-4" />
              Anterior
            </button>
            
            <button
              onClick={nextStep}
              className={`${styles.navButton} ${styles.nextButton}`}
            >
              {currentStep === tourSteps.length - 1 ? 'Finalizar' : 'Próximo'}
              {currentStep < tourSteps.length - 1 && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default HabitDetailsTour;
